"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Consent banner — the CMP GDPR/TDDDG requires before PostHog or the Meta Pixel may load. Two independent,
 * un-pre-ticked purposes ("Produktanalyse (PostHog)", "Marketing (Meta Pixel)"), three actions of equal
 * prominence where it matters: "Ablehnen" and "Alle akzeptieren" look the same (no dark pattern), and
 * "Auswahl speichern" applies exactly the boxes the visitor ticked. Neither SDK is ever fetched before one
 * of them is pressed — lib/analytics.ts only loads them from the consent-change listeners this banner triggers.
 *
 * It shows while EITHER purpose is undecided, so a visitor who answered before the Marketing purpose existed
 * is asked once about it; a purpose that is already decided is pre-filled from the stored choice.
 */
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { useI18n } from "@/i18n"
import {
  getAdsConsent,
  getMarketingConsent,
  onAdsConsentChange,
  onMarketingConsentChange,
  setAdsConsent,
  setMarketingConsent,
  type MarketingConsent,
} from "@/lib/consentGate"
import { LEGAL_PATHS } from "@/lib/legalRoutes"

function CheckboxRow({
  id,
  checked,
  onChange,
  label,
}: {
  id: string
  checked: boolean
  onChange: (next: boolean) => void
  label: string
}) {
  return (
    <div className="flex min-h-10 items-center gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="size-5 shrink-0 rounded border-line text-green focus-visible:ring-2 focus-visible:ring-green"
      />
      <label htmlFor={id} className="text-sm font-semibold text-ink">
        {label}
      </label>
    </div>
  )
}

export function ConsentBanner() {
  const { t } = useI18n()
  const [analytics, setAnalytics] = useState<MarketingConsent>(() => getMarketingConsent())
  const [ads, setAds] = useState<MarketingConsent>(() => getAdsConsent())
  // Un-pre-ticked unless the purpose was already granted.
  const [analyticsChecked, setAnalyticsChecked] = useState(() => getMarketingConsent() === "granted")
  const [adsChecked, setAdsChecked] = useState(() => getAdsConsent() === "granted")

  // The choices can change after the first decision (footer "Cookie-Einstellungen" clears both; the privacy
  // page's switches set one): the banner follows, so it re-opens exactly when either state is "unset".
  useEffect(
    () =>
      onMarketingConsentChange(next => {
        setAnalytics(next)
        setAnalyticsChecked(next === "granted")
      }),
    []
  )
  useEffect(
    () =>
      onAdsConsentChange(next => {
        setAds(next)
        setAdsChecked(next === "granted")
      }),
    []
  )

  if (analytics !== "unset" && ads !== "unset") return null

  function decide(analyticsGranted: boolean, adsGranted: boolean) {
    setMarketingConsent(analyticsGranted ? "granted" : "denied")
    setAdsConsent(adsGranted ? "granted" : "denied")
  }

  return (
    <div
      role="region"
      aria-label={t("consent.webBody")}
      className="fixed inset-x-0 bottom-0 z-[60] flex justify-center px-4 pb-4"
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-3 overflow-y-auto rounded-bb-xl border border-line bg-paper p-4 shadow-lg">
        <p className="text-sm leading-relaxed text-ink-2">
          {t("consent.webBody")}{" "}
          <a
            href={LEGAL_PATHS.privacy}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 text-ink hover:text-green"
          >
            {t("auth.privacyLink")}
          </a>
        </p>
        <div className="flex flex-col gap-1">
          <CheckboxRow
            id="consent-analytics"
            checked={analyticsChecked}
            onChange={setAnalyticsChecked}
            label={t("consent.analyticsOption")}
          />
          <CheckboxRow
            id="consent-ads"
            checked={adsChecked}
            onChange={setAdsChecked}
            label={t("consent.adsOption")}
          />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button type="button" className="min-h-10" onClick={() => decide(false, false)}>
            {t("consent.decline")}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="min-h-10"
            onClick={() => decide(analyticsChecked, adsChecked)}
          >
            {t("consent.save")}
          </Button>
          <Button type="button" className="min-h-10" onClick={() => decide(true, true)}>
            {t("consent.acceptAll")}
          </Button>
        </div>
      </div>
    </div>
  )
}
